import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { hu } from 'date-fns/locale';
import { apiService, getApiErrorMessage } from '../services/apiService';

interface IktatosData {
  iktatoszamErteke: string;
  iktatasIranya: string;
  datum: Date | null;
  munkaszamId: number | null;
  munkaTargya: string;
  iktatasTipusa: string;
  iratTargya: string;
  cimzettKuldo: string;
  iratHelye: string;
  ugyintezoId: string;
}

interface Munkaszam {
  id: number;
  munkaszamErteke: string;
  munkaTargya: string;
}

interface User {
  id: string;
  userName: string;
  displayName: string;
}

interface Megrendelo {
  id: number;
  cegnev: string;
}

interface AddIktatosDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddIktatosDialog: React.FC<AddIktatosDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<IktatosData>({
    iktatoszamErteke: 'Automatikusan generálódik',
    iktatasIranya: '',
    datum: new Date(),
    munkaszamId: null,
    munkaTargya: '',
    iktatasTipusa: '',
    iratTargya: '',
    cimzettKuldo: '',
    iratHelye: '',
    ugyintezoId: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Autocomplete states
  const [munkaszamOptions, setMunkaszamOptions] = useState<Munkaszam[]>([]);
  const [munkaszamLoading, setMunkaszamLoading] = useState(false);
  const [munkaszamInputValue, setMunkaszamInputValue] = useState('');
  
  const [userOptions, setUserOptions] = useState<User[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userInputValue, setUserInputValue] = useState('');
  
  const [cimzettOptions, setCimzettOptions] = useState<(User | Megrendelo)[]>([]);
  const [cimzettLoading, setCimzettLoading] = useState(false);
  const [cimzettInputValue, setCimzettInputValue] = useState('');

  // Reset iktatószám display when dialog opens
  useEffect(() => {
    if (open) {
      setFormData(prev => ({ 
        ...prev, 
        iktatoszamErteke: 'Automatikusan generálódik'
      }));
    }
  }, [open]);

  // Munkaszám keresés
  const searchMunkaszamok = async (query: string) => {
    if (!query || query.length < 2) {
      setMunkaszamOptions([]);
      return;
    }

    setMunkaszamLoading(true);
    try {
      const response = await apiService.getData('munkaszamok');
      const filtered = response.filter((munkaszam: any) => 
        munkaszam.munkaszam?.toLowerCase().includes(query.toLowerCase()) ||
        munkaszam.munkaTargya?.toLowerCase().includes(query.toLowerCase())
      );
      setMunkaszamOptions(filtered.map((m: any) => ({
        id: m.id,
        munkaszamErteke: m.munkaszam || `${m.ev}-${m.sorszam}/${m.id}`,
        munkaTargya: m.munkaTargya || ''
      })));
    } catch (error) {
      console.error('Error searching munkaszamok:', error);
    } finally {
      setMunkaszamLoading(false);
    }
  };

  // User keresés (ügyintéző)
  const searchUsers = async (query: string) => {
    if (!query || query.length < 2) {
      setUserOptions([]);
      return;
    }

    setUserLoading(true);
    try {
      const response = await apiService.get(`/Users/search?query=${encodeURIComponent(query)}`);
      setUserOptions(response.data || []);
    } catch (error) {
      console.error('Error searching users:', error);
      setUserOptions([]);
      setError(getApiErrorMessage(error, 'Nem sikerült az ügyintézők keresése.'));
    } finally {
      setUserLoading(false);
    }
  };

  // Címzett keresés (users + megrendelők)
  const searchCimzett = async (query: string) => {
    if (!query || query.length < 2) {
      setCimzettOptions([]);
      return;
    }

    setCimzettLoading(true);
    try {
      const [usersResponse, megrendelokResponse] = await Promise.all([
        apiService.get(`/Users/search?query=${encodeURIComponent(query)}`),
        apiService.get(`/Megrendelok/search?query=${encodeURIComponent(query)}`)
      ]);

      const users: User[] = usersResponse.data || [];
      const megrendelok: Megrendelo[] = megrendelokResponse.data || [];

      // Combine results
      const combined = [
        ...users.map(u => ({ ...u, type: 'user', displayName: u.displayName || u.userName })),
        ...megrendelok.map((m: any) => ({ ...m, type: 'megrendelo', displayName: m.cegnev }))
      ];

      setCimzettOptions(combined);
    } catch (error) {
      console.error('Error searching címzett:', error);
      setCimzettOptions([]);
      setError(getApiErrorMessage(error, 'Nem sikerült a címzettek keresése.'));
    } finally {
      setCimzettLoading(false);
    }
  };

  const handleMunkaszamChange = (munkaszam: Munkaszam | null) => {
    setFormData(prev => ({
      ...prev,
      munkaszamId: munkaszam?.id || null,
      munkaTargya: munkaszam?.munkaTargya || ''
    }));
    if (error) setError(null);
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.iktatasIranya) {
      setError('Iktatás irányának kiválasztása kötelező!');
      return;
    }

    if (!formData.datum) {
      setError('Dátum megadása kötelező!');
      return;
    }

    if (!formData.munkaszamId) {
      setError('Munkaszám kiválasztása kötelező!');
      return;
    }

    if (!formData.iktatasTipusa) {
      setError('Iktatás típusának kiválasztása kötelező!');
      return;
    }

    if (!formData.iratTargya.trim()) {
      setError('Irat tárgya megadása kötelező!');
      return;
    }

    if (!formData.ugyintezoId) {
      setError('Ügyintéző kiválasztása kötelező!');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create the data object for the API
      const iktatosData = {
        // iktatoszamErteke: A backend automatikusan generálja
        iktatasIranya: formData.iktatasIranya,
        datum: formData.datum!,
        munkaTargya: formData.munkaTargya,
        iktatasTipusa: formData.iktatasTipusa,
        iratTargya: formData.iratTargya.trim(),
        cimzettKuldo: formData.cimzettKuldo.trim(),
        iratHelye: formData.iratHelye.trim(),
        ugyintezoId: formData.ugyintezoId,
        munkaszamId: formData.munkaszamId!,
      };

      await apiService.createData('iktatas', iktatosData);
      
      // Reset form
      resetForm();
      
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error creating iktatos:', err);
      setError(getApiErrorMessage(err, 'Hiba történt az iktatás létrehozása során.'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      iktatoszamErteke: 'Automatikusan generálódik',
      iktatasIranya: '',
      datum: new Date(),
      munkaszamId: null,
      munkaTargya: '',
      iktatasTipusa: '',
      iratTargya: '',
      cimzettKuldo: '',
      iratHelye: '',
      ugyintezoId: '',
    });
    setError(null);
    setMunkaszamOptions([]);
    setUserOptions([]);
    setCimzettOptions([]);
    setMunkaszamInputValue('');
    setUserInputValue('');
    setCimzettInputValue('');
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu}>
      <Dialog 
        open={open} 
        onClose={handleCancel} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '700px' }
        }}
      >
        <DialogTitle>Új Iktatás Hozzáadása</DialogTitle>
        
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Iktatás iránya */}
              <FormControl fullWidth error={!!error && !formData.iktatasIranya}>
                <InputLabel>Iktatás iránya *</InputLabel>
                <Select
                  value={formData.iktatasIranya}
                  label="Iktatás iránya *"
                  onChange={(event) => {
                    setFormData(prev => ({ ...prev, iktatasIranya: event.target.value }));
                    if (error) setError(null);
                  }}
                >
                  <MenuItem value="be">Be</MenuItem>
                  <MenuItem value="ki">Ki</MenuItem>
                </Select>
              </FormControl>

              {/* Dátum */}
              <DatePicker
                label="Dátum *"
                value={formData.datum}
                onChange={(newValue) => {
                  setFormData(prev => ({ ...prev, datum: newValue }));
                  if (error) setError(null);
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!error && !formData.datum,
                    helperText: !!error && !formData.datum ? 'Dátum megadása kötelező' : '',
                  }
                }}
              />

              {/* Munkaszám */}
              <Autocomplete
                options={munkaszamOptions}
                getOptionLabel={(option) => option.munkaszamErteke}
                loading={munkaszamLoading}
                inputValue={munkaszamInputValue}
                onInputChange={(event, newInputValue) => {
                  setMunkaszamInputValue(newInputValue);
                  searchMunkaszamok(newInputValue);
                }}
                onChange={(event, newValue) => handleMunkaszamChange(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Munkaszám *"
                    error={!!error && !formData.munkaszamId}
                    helperText={!!error && !formData.munkaszamId ? 'Munkaszám kiválasztása kötelező' : 'Kezdje el gépelni a munkaszámot...'}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {munkaszamLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <div style={{ fontWeight: 'bold' }}>{option.munkaszamErteke}</div>
                      <div style={{ fontSize: '0.8em', color: 'gray' }}>{option.munkaTargya}</div>
                    </Box>
                  </Box>
                )}
              />

              {/* Munka tárgya (automatikus) */}
              <TextField
                label="Munka tárgya"
                fullWidth
                value={formData.munkaTargya}
                disabled
                helperText="Automatikusan kitöltődik a kiválasztott munkaszám alapján"
              />

              {/* Iktatás típusa */}
              <FormControl fullWidth error={!!error && !formData.iktatasTipusa}>
                <InputLabel>Iktatás típusa *</InputLabel>
                <Select
                  value={formData.iktatasTipusa}
                  label="Iktatás típusa *"
                  onChange={(event) => {
                    setFormData(prev => ({ ...prev, iktatasTipusa: event.target.value }));
                    if (error) setError(null);
                  }}
                >
                  <MenuItem value="Email">Email</MenuItem>
                  <MenuItem value="Levelezés">Levelezés</MenuItem>
                  <MenuItem value="Dokumentum">Dokumentum</MenuItem>
                </Select>
              </FormControl>

              {/* Irat tárgya */}
              <TextField
                label="Irat tárgya *"
                fullWidth
                multiline
                rows={2}
                value={formData.iratTargya}
                onChange={(event) => {
                  setFormData(prev => ({ ...prev, iratTargya: event.target.value }));
                  if (error) setError(null);
                }}
                error={!!error && !formData.iratTargya.trim()}
                helperText={!!error && !formData.iratTargya.trim() ? 'Irat tárgya megadása kötelező' : ''}
              />

              {/* Címzett */}
              <Autocomplete
                options={cimzettOptions}
                getOptionLabel={(option: any) => option.displayName || option.cegnev || option.userName}
                loading={cimzettLoading}
                inputValue={cimzettInputValue}
                onInputChange={(event, newInputValue) => {
                  setCimzettInputValue(newInputValue);
                  searchCimzett(newInputValue);
                }}
                onChange={(event, newValue: any) => {
                  setFormData(prev => ({ 
                    ...prev, 
                    cimzettKuldo: newValue ? (newValue.displayName || newValue.cegnev || newValue.userName) : '' 
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Címzett"
                    helperText="Kezdje el gépelni a nevet vagy cégnevet..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {cimzettLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option: any) => (
                  <Box component="li" {...props}>
                    <Box>
                      <div>{option.displayName || option.cegnev || option.userName}</div>
                      <div style={{ fontSize: '0.8em', color: 'gray' }}>
                        {option.type === 'user' ? 'Felhasználó' : 'Megrendelő'}
                      </div>
                    </Box>
                  </Box>
                )}
              />

              {/* Irat helye */}
              <TextField
                label="Irat helye"
                fullWidth
                value={formData.iratHelye}
                onChange={(event) => {
                  setFormData(prev => ({ ...prev, iratHelye: event.target.value }));
                }}
              />

              {/* Ügyintéző */}
              <Autocomplete
                options={userOptions}
                getOptionLabel={(option) => option.displayName}
                loading={userLoading}
                inputValue={userInputValue}
                onInputChange={(event, newInputValue) => {
                  setUserInputValue(newInputValue);
                  searchUsers(newInputValue);
                }}
                onChange={(event, newValue) => {
                  setFormData(prev => ({ ...prev, ugyintezoId: newValue?.id || '' }));
                  if (error) setError(null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ügyintéző *"
                    error={!!error && !formData.ugyintezoId}
                    helperText={!!error && !formData.ugyintezoId ? 'Ügyintéző kiválasztása kötelező' : ''}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {userLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleCancel} 
            disabled={loading}
            variant="outlined"
          >
            Mégse
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Létrehozás...' : 'Létrehozás'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AddIktatosDialog;
